import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { BuildingModel } from '../../../../../../core/models/building.model';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { FormModalBaseComponent } from '../../../../../../core/base/components/form-modal-base/form-modal-base.component';
import { BuildingService } from '../../building.service';
import { map } from 'rxjs/operators';
import { ErrorApi } from '../../../../../../core/models/api/error-api';
import { EnergyIdService } from '../../../energy-id/energy-id.service';
import { convertToBuildingRelationEnergyId, EnergyIdModel } from '../../../../../../core/models/energy-id.model';
import icClose from '@iconify/icons-ic/twotone-close';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import icAdd from '@iconify/icons-ic/twotone-add';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { EnergyIdCreateUpdateDeleteComponent } from '../../../energy-id/energy-id-create-update-delete/energy-id-create-update-delete.component';
import { GenericEnergyIdTypeEnergyEnum } from '../../../../../../core/enums/generic-energy-id.enum';

@Component({
  selector: 'vex-building-energies-create-update',
  templateUrl: './building-energies-create-update.component.html',
  styleUrls: ['./building-energies-create-update.component.scss'],
})
export class BuildingEnergiesCreateUpdateComponent implements OnInit {
  filters = {};
  ENUM_PERMISSION = ENUM_PERMISSIONS;

  form: UntypedFormGroup;
  mode: CrudModeEnum;
  icClose = icClose;
  icAdd = icAdd;

  loading = false;
  defaults: any;
  successCreateMessage: string;
  successUpdateMessage: string;
  successDeleteMessage: string;
  errorCreateMessage: string;
  errorUpdateMessage: string;
  errorDeleteMessage: string;

  energyIds$: Observable<EnergyIdModel[]>;
  building: BuildingModel;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<FormModalBaseComponent<BuildingModel>>,
    public service: BuildingService,
    private energyIdService: EnergyIdService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.mode = this.data.mode;
    this.defaults = this.data.energy;
    this.building = this.data.building;

    this.filters = {
      requiredFields: ['*'],
      buildingIds: [this.building._id],
      customSearchFields: ['energyIdId', 'genericEnergyId.fieldLabel'],
    };

    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(this.defaults?._id),
      energyIdId: new UntypedFormControl(this.defaults, Validators.required),
    });

    this.energyIdsAutoCompleteChange();
  }

  isCreateMode() {
    return this.mode === CrudModeEnum.Create;
  }

  isUpdateMode() {
    return this.mode === CrudModeEnum.Update;
  }

  isDeleteMode() {
    return this.mode === CrudModeEnum.Delete;
  }

  save() {
    if (this.isCreateMode()) {
      this.createItem();
    } else if (this.isUpdateMode()) {
      this.updateItem();
    }
  }

  setErrorsMessages(errors: Array<ErrorApi>) {
    errors?.forEach(error => {
      let err = this.form.controls[error.property].errors;
      if (err === null) {
        err = { messages: [] };
      }
      err.messages.push(error.message);
      this.form.controls[error.property].setErrors(err);
    });
    this.form.markAllAsTouched();
  }

  createItem() {
    if (this.form.valid) {
      this.loading = true;
      const data = this.form.getRawValue();
      this.service.createEnergy(this.building._id, { energyIdId: data.energyIdId?._id }).subscribe(
        res => {
          console.log('Success:', res);
          this.close(res.data);
          this.loading = false;
        },
        error => {
          console.error('Error:', error);
          this.setErrorsMessages(error.error?.errors);
          this.form.markAllAsTouched();
          this.loading = false;
        },
      );
    } else {
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }

  updateItem() {
    if (this.form.valid) {
      this.loading = true;
      const data = this.form.getRawValue();
      this.service.updateEnergy(this.building._id, { _id: data._id, newEnergyId: data.energyIdId?._id }).subscribe(
        res => {
          this.close(res.data);
          this.loading = false;
        },
        error => {
          this.setErrorsMessages(error.error?.errors);
          this.form.markAllAsTouched();
          this.loading = false;
        },
      );
    } else {
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }

  deleteItem() {
    if (this.defaults) {
      this.loading = true;
      this.service.deleteEnergy(this.building._id, this.defaults._id).subscribe(
        res => {
          this.close(res);
          this.loading = false;
        },
        error => {
          this.loading = false;
        },
      );
    }
  }

  close(res?) {
    this.dialogRef.close(res);
  }

  energyIdsAutoCompleteChange(state: any = '') {
    this.energyIds$ = this.energyIdService.findAll(null, null, 'energyIdId', 'asc', state, this.filters).pipe(
      map(x => {
        // Récupération des IDs des énergies déjà présentes dans le building
        const existingEnergyIds = this.building.energies?.map(e => e._id.toString()) || [];

        // Filtrage des énergies
        return x.data.filter(energyId => {
          const energyIdStr = energyId._id.toString();

          // En mode Update : on garde l'énergie actuelle en cours de modification
          if (this.isUpdateMode() && this.defaults?._id?.toString() === energyIdStr) {
            return true;
          }

          // On exclut les énergies déjà présentes dans le building
          return !existingEnergyIds.includes(energyIdStr);
        });
      }),
    );
  }

  async addEnergyId() {
    const buildingRelation = convertToBuildingRelationEnergyId(this.building);

    const element = {
      buildings: [buildingRelation],
      establishments: this.building.establishments,
      favoriteBuilding: buildingRelation,
    };

    this.dialog
      .open(EnergyIdCreateUpdateDeleteComponent, {
        width: '700px',
        data: {
          defaults: element,
          mode: CrudModeEnum.Custom,
        },
      })
      .afterClosed()
      .subscribe((energyId: EnergyIdModel) => {
        if (energyId) {
          this.energyIdsAutoCompleteChange();
          this.form.controls.energyIdId.setValue(energyId);
        }
      });
  }
}
